// ──────────────────────────────────────────────────────────────────────────
// GA4 CONVERSION FUNNEL TRACKING
//
// Typed helpers for tracking the giga.com visitor → waitlist signup funnel.
// Only fires when window.gtag is available (GA4 script loaded). All event
// names use snake_case for consistent GA4 dashboards.
//
// Funnel:
//   Page visit → CTA click → Signup page → Modal open → Email submit → Done
// ──────────────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

type EventParams = Record<string, string | number | boolean | undefined>;

// ── Event names ────────────────────────────────────────────────────────────
// Keep these as a const object so consuming code gets autocomplete + type safety.
export const AnalyticsEvent = {
  CTA_CLICK: 'cta_click',
  SIGNUP_PAGE_VIEWED: 'signup_page_viewed',
  WAITLIST_MODAL_OPENED: 'waitlist_modal_opened',
  WAITLIST_SUBMITTED: 'waitlist_submitted',
  WAITLIST_COMPLETED: 'waitlist_completed',
  WAITLIST_ERROR: 'waitlist_error',
} as const;

export type AnalyticsEventType =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

// ── Core send function ─────────────────────────────────────────────────────

/** Fire a GA4 event. Safe to call on the server / before gtag is loaded. */
export function trackEvent(eventName: string, params?: EventParams) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
}

// ── Funnel helpers ─────────────────────────────────────────────────────────

/** Someone clicked a CTA button. `location` identifies which one (hero, nav,
 * closing, pricing-card, etc.). */
export function trackCtaClick(location: string, ctaText?: string) {
  trackEvent(AnalyticsEvent.CTA_CLICK, {
    cta_location: location,
    cta_text: ctaText,
  });
}

/** Someone landed on the /signup page. */
export function trackSignupPageView(source?: string) {
  trackEvent(AnalyticsEvent.SIGNUP_PAGE_VIEWED, {
    source: source || 'direct',
  });
}

/** The waitlist modal appeared on screen. */
export function trackWaitlistModalOpen(referredBy?: string | null) {
  trackEvent(AnalyticsEvent.WAITLIST_MODAL_OPENED, {
    was_referred: referredBy ? 'yes' : 'no',
  });
}

/** User submitted their email to the waitlist form. */
export function trackWaitlistSubmitted(email?: string) {
  trackEvent(AnalyticsEvent.WAITLIST_SUBMITTED, {
    email_domain: emailDomain(email),
  });
}

/** Waitlist API returned success. */
export function trackWaitlistCompleted(
  status: string,
  position?: number,
) {
  trackEvent(AnalyticsEvent.WAITLIST_COMPLETED, {
    signup_status: status,
    queue_position: position,
  });
}

/** Waitlist API returned an error or timed out. */
export function trackWaitlistError(error?: string) {
  trackEvent(AnalyticsEvent.WAITLIST_ERROR, {
    error_message: error ? error.slice(0, 100) : 'unknown',
  });
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** Extract just the domain from an email for privacy-safe reporting. */
function emailDomain(email?: string): string | undefined {
  if (!email) return undefined;
  const parts = email.split('@');
  return parts.length === 2 ? parts[1] : undefined;
}
